import { useEffect, useRef, useState, type ReactNode } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  ArrowLeft,
  ArrowRight,
  Camera,
  CheckCircle2,
  Clapperboard,
  ImagePlus,
  ShieldAlert,
  UserRound,
  XCircle,
} from "lucide-react";

import { Badge } from "@/shared/ui/badge";
import { Button } from "@/shared/ui/button";
import { Card, CardContent } from "@/shared/ui/card";
import { Checkbox } from "@/shared/ui/checkbox";
import { Input } from "@/shared/ui/input";
import { Label } from "@/shared/ui/label";
import { PageHeader } from "@/shared/ui/PageHeader";
import { StepIndicator } from "@/shared/ui/StepIndicator";
import { toast } from "@/shared/hooks/use-toast";
import { cn } from "@/shared/lib/utils";
import {
  characterService,
  type CharacterDto,
  type FacePrecheckResult,
} from "@/entities/character/api/characterService";
import { dhProfileService } from "@/entities/dh-profile/api/dhProfileService";
import { VIDEO_ACCEPT } from "@/shared/lib/uploadImageFormats";
import { checkVideoFile, type VideoIssue } from "@/shared/lib/videoFileCheck";
import { errorToast, errorText } from "@/shared/lib/apiErrors";
import { facePrecheckReasonText } from "../lib/facePrecheckReason";

/**
 * P0 三步流不变(SEEDANCE 真人出镜 P0,0914 车 D5):走 vapeur 免扫码通道,这一批不加步骤。
 * P1: 切 `volcano_h5` 官方通道时,这里要插一步「扫码认证」(在「授权入库」与「声纹」之间,
 * H5 二维码 → 轮询 → 绑真人组,见 `RealPersonVerifyDialog`,任务书附「P1 触发时的激活清单」)——
 * 到时候改的是这个数组 + StepIndicator 的 current 映射,别的三步渲染逻辑不用动。
 */
const STEPS = ["拍摄指引", "上传体检", "授权入库"] as const;
/** 第 4 步只在 App 层注入了录音卡时才存在(注入见 App.tsx;无注入=老三步流,行为不变)。 */
const STEPS_WITH_VOICE = [...STEPS, "声纹(可选)"] as const;

/** 上游人脸链路要求原图 ≥640px;更小的图先在本地拦下,别浪费一次预检往返。 */
const MIN_EDGE = 640;

/**
 * 老板 IP 采集流(packs W2 T4,设计 §2.3;2026-08-10 闭环批扩为一站式建齐三资产)。
 * 指引 → 上传体检(face-precheck 前置到入库,脸太小/太暗/多人**当场报**,不等出片
 * IMAGE_CHECK_FAILED;★定妆照必传 —— 后端 from-avatar-profile 拒 video kind,定妆图必须照片)
 * + 口播视频可选槽(同 consent 存进「我的形象」kind=video,出片走视频换口播/可用原声)
 * → consent 存证 → enroll → 升格 → 可选第 4 步现场克隆声纹并挂到人物(voiceEnrollSlot 注入)。
 * 真人红线:本人授权入库 → 租户内全公司共用、跨公司隔离(0915 批示一撤掉了 owner-only)+ AIGC 标识全程。
 */
export default function CharacterRealCreatePage({
  voiceEnrollSlot,
}: {
  /**
   * 「第 4 步:顺手克隆声纹」的录音卡,由 App.tsx 路由级注入(FSD:characters 与
   * digital-human 互不 import,组合上提 app 层,同 DhRealAssetsPanel 范式)。
   * 录成后回调 voiceProfileId,本页负责挂到刚建的人物上。不注入则维持老三步流。
   */
  voiceEnrollSlot?: (onEnrolled: (voiceProfileId: number) => void) => ReactNode;
}) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  /**
   * ★0915 车 F(SEEDANCE 真人出镜 P0.5):真人出镜专属页尾卡「＋新建出镜人」带
   * `?returnTo=` 跳进来 —— 建完人物后应该送回那一页继续选人,不是老默认的人物详情页。
   * 没带这个参数 = 老行为原样不变(直落 `/characters/{id}`)。
   * ★这条路径只做本地相对路径白名单(以 `/` 开头),不接受外部 URL——避免开放重定向。
   */
  const returnTo = searchParams.get("returnTo")?.startsWith("/")
    ? searchParams.get("returnTo")
    : null;
  const exitTo = (characterId: number) => returnTo || `/characters/${characterId}`;
  const [step, setStep] = useState(1);
  const [name, setName] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [tooSmall, setTooSmall] = useState(false);
  const [precheck, setPrecheck] = useState<FacePrecheckResult | null>(null);
  const [consent, setConsent] = useState(false);
  const [videoFile, setVideoFile] = useState<File | null>(null);
  /** 0915 老板走查「显示不协调」:视频槽升级成与照片同构的预览框,需要 objectURL 预览。 */
  const [videoPreviewUrl, setVideoPreviewUrl] = useState<string | null>(null);
  /** D5③:videoFile 的体检结果(超限即时红字,§B4 同份约束)。null = 未选或已达标。 */
  const [videoIssue, setVideoIssue] = useState<VideoIssue | null>(null);
  const [videoChecking, setVideoChecking] = useState(false);
  const [created, setCreated] = useState<CharacterDto | null>(null);
  const [voiceSaving, setVoiceSaving] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const videoInputRef = useRef<HTMLInputElement>(null);
  const steps = voiceEnrollSlot ? STEPS_WITH_VOICE : STEPS;

  // 租户治理开关:真人照片上传关闭时整条流阻断(与分身舱同闸 dhAllowRealPhoto)。
  const gateQuery = useQuery({
    queryKey: ["tenant-settings", "dh-gate"],
    queryFn: () => dhProfileService.getTenantGate(),
  });
  const allowPhoto = gateQuery.data?.dhAllowRealPhoto !== false;

  useEffect(() => () => void (previewUrl && URL.revokeObjectURL(previewUrl)), [previewUrl]);
  useEffect(
    () => () => void (videoPreviewUrl && URL.revokeObjectURL(videoPreviewUrl)),
    [videoPreviewUrl]
  );

  const precheckMutation = useMutation({
    mutationFn: (f: File) => characterService.facePrecheck(f),
    onSuccess: setPrecheck,
    onError: (e) => toast(errorToast(e, { fallbackTitle: "体检没跑成" })),
  });

  /**
   * ★0915 车 F 增补(多图 A/B 已判阳):再传几张不同角度照片,人脸更稳 —— 露出既有的
   * `characterService.uploadMaterial(id, file, "identity")` 多图能力(此前只有详情页
   * 补拍才用得到),采集流第一次建档时就能一口气带上,不用建完人再回来分开传。
   * ★不改流程结构:主图(第一张)照旧走本地尺寸检查 + face-precheck 那条路,多传的几张
   * 单纯排队等 enroll 成功后再逐张挂 identity 槽(最多再收 2 张,对齐引导文案「再传 2 张」)。
   */
  const [extraFiles, setExtraFiles] = useState<File[]>([]);
  const MAX_EXTRA_PHOTOS = 2;

  const pickFile = async (files: FileList | File[] | null | undefined) => {
    const list = files ? Array.from(files) : [];
    const f = list[0];
    if (!f) return;
    setExtraFiles(list.slice(1, 1 + MAX_EXTRA_PHOTOS));
    setPrecheck(null);
    setFile(f);
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    const url = URL.createObjectURL(f);
    setPreviewUrl(url);
    // 本地量尺寸:短边 <640 直接提示传原图(微信/截图常见压缩),不打预检。
    const small = await new Promise<boolean>((resolve) => {
      const img = new Image();
      img.onload = () => resolve(Math.min(img.naturalWidth, img.naturalHeight) < MIN_EDGE);
      img.onerror = () => resolve(false);
      img.src = url;
    });
    setTooSmall(small);
    if (!small) precheckMutation.mutate(f);
  };

  /** D5③:选口播视频即当场体检(不等提交才知道超限),形制同 pickFile。 */
  const pickVideo = async (f: File | undefined) => {
    if (!f) return;
    setVideoFile(f);
    if (videoPreviewUrl) URL.revokeObjectURL(videoPreviewUrl);
    setVideoPreviewUrl(URL.createObjectURL(f));
    setVideoIssue(null);
    setVideoChecking(true);
    try {
      setVideoIssue(await checkVideoFile(f));
    } finally {
      setVideoChecking(false);
    }
  };

  const enroll = useMutation({
    mutationFn: async () => {
      /**
       * ★0915 归一批 B:两步链退役。
       *
       * 旧路是「先 enroll 形象档案 → 再 fromAvatarProfile 升格建人物(形象照复制成主定妆图)」,
       * 而升格口强制 `avatarProfile.kind=photo`、对 video **直接拒** —— 这正是老板实报
       * 「只有视频他无法上传」的根:整条真人线都建立在"一定先有一张照片"上。
       *
       * 归一后素材直接挂人物,于是流程变成「建人物 → 传素材」,而且
       * **照片与视频都只是素材,谁先谁后、有没有另一个,都不再是前置条件**。
       */
      const character = await characterService.create({ name: name.trim(), type: "real" });

      // 主定妆图(有就传,并钉成主图);只有视频的人物这一步整个跳过。
      let primary: { id: number } | null = null;
      if (file) {
        primary = await characterService.uploadMaterial(character.id, file, "identity");
        await characterService.setPrimaryMaterial(character.id, primary.id);
      }

      // 口播视频:同一张表、同一个 identity 槽,只是 kind=video(后端按 contentType 判)。
      // ★失败不否决人物建档(人物已成立)—— warning 点名,别静默。
      // D5③:体检闸判 blocksSubmit 的文件直接跳过,不白发一个注定失败的请求;
      // 「超长会被截」那条不算数(ai 侧 B4 会截前 15s 复用)。
      let videoSaved: boolean | null = null;
      if (videoFile && videoIssue?.blocksSubmit) {
        videoSaved = false;
      } else if (videoFile) {
        try {
          const v = await characterService.uploadMaterial(character.id, videoFile, "identity");
          // 没有照片时,视频就是这个人物的封面 —— 卡面按 kind 渲染成 <video>。
          if (!primary) await characterService.setPrimaryMaterial(character.id, v.id);
          videoSaved = true;
        } catch {
          videoSaved = false;
        }
      }

      // 多传的几张角度照逐张挂 identity 槽(锦上添花,不否决建档)——
      // 单张失败不连累其余,也不连累人物本体;数一遍成了几张,失败了如实说。
      let extraPhotosSaved = 0;
      for (const ef of extraFiles) {
        try {
          await characterService.uploadMaterial(character.id, ef, "identity");
          extraPhotosSaved++;
        } catch {
          /* 单张失败不阻断,数量里如实少算 */
        }
      }
      return { character, videoSaved, extraPhotosSaved, extraPhotosTotal: extraFiles.length };
    },
    onSuccess: ({ character, videoSaved, extraPhotosSaved, extraPhotosTotal }) => {
      // ★多传角度照的结果:全成功不单独提(锦上添花,别抢主提示的注意力);
      // 有失败才如实追一句,免得用户以为都存上了。
      const extraNote =
        extraPhotosTotal > 0 && extraPhotosSaved < extraPhotosTotal
          ? ` 另外多传的 ${extraPhotosTotal} 张角度照只存上 ${extraPhotosSaved} 张,可去详情页补传。`
          : "";
      if (videoSaved === false) {
        toast({
          title: "真人人物已入库,但口播视频没存上",
          // D5③:体检闸拦下的文件把具体原因带出来(比通用「没存上」更有用);
          // 网络/后端失败(videoIssue 没标记)仍用通用文案。
          description:
            (videoIssue?.blocksSubmit && videoFile
              ? `视频没存上:${videoIssue.text} —— 换一个文件,可稍后在出片页重传,人物档案不受影响。`
              : "口播视频没存上(可稍后在出片页上传口重传),人物档案不受影响。") + extraNote,
        });
      } else {
        toast({
          title: "真人人物已入库",
          description:
            (videoSaved === true
              ? "形象照已钉为主定妆图;口播视频已存进「我的素材」,出片可直接选用。"
              : "形象照已钉为主定妆图,去详情页补角色卡与更多物料。") + extraNote,
        });
      }
      // 注入了录音卡 → 进第 4 步顺手克隆声纹;没有注入维持老行为直落详情页(或 returnTo)。
      if (voiceEnrollSlot) {
        setCreated(character);
        setStep(4);
      } else {
        navigate(exitTo(character.id));
      }
    },
    onError: (e) =>
      // owner-only / consent / 配额等服务端拒绝,失败信息直接展示(启动书口径)。
      toast(errorToast(e, { fallbackTitle: "入库失败" })),
  });

  /** 第 4 步:录音卡回调 → 把声纹挂到刚建的人物上(update 语义:没提的字段不动)。 */
  const linkVoice = async (voiceProfileId: number) => {
    if (!created) return;
    setVoiceSaving(true);
    try {
      await characterService.update(created.id, { name: created.name, voiceProfileId });
      toast({
        title: `声纹已挂到「${created.name}」`,
        description: "出片「按人物选」即可同时带出形象与声音。",
      });
      navigate(exitTo(created.id));
    } catch (e) {
      // 声纹本身已克隆成功(躺在声音库里),只是挂钩失败 —— 说清楚,别让用户以为白录了。
      toast({
        title: "声纹已克隆,但挂到人物失败",
        description: `${errorText(e)} —— 声纹已在声音库,可去人物详情「基本信息」手动挂。`,
        variant: "destructive",
      });
    } finally {
      setVoiceSaving(false);
    }
  };

  const precheckBlocked = tooSmall || (precheck != null && precheck.checked && !precheck.pass);
  const canNext2 = !!file && !precheckBlocked && !precheckMutation.isPending;

  return (
    // 2026-08-10 满屏宽批跟进(先例 d9cf1970 舱页统一 w-full):此前 max-w-3xl 版心在 1920
    // 两侧各空 ~500px,用户报「没充满整个屏幕」。容器放开,步骤一双栏用掉宽度;
    // 表单控件自身限宽(输入框不是越宽越好),文本块限 max-w-2xl 保行长可读。
    <div className="w-full space-y-6">
      <PageHeader
        icon={UserRound}
        title="真人人物(老板 IP)"
        subtitle="用一张规范照片建档:体检当场报、授权先存证,入库即是主定妆图。"
        actions={
          <Button variant="outline" onClick={() => navigate("/characters")}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            返回人物库
          </Button>
        }
      />

      <StepIndicator
        steps={steps}
        current={step}
        label="真人人物采集流程"
        domainAttr="data-character-real-steps"
      />

      {!allowPhoto && (
        <Card className="border-destructive/50">
          <CardContent className="flex items-start gap-3 py-4 text-sm">
            <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
            <div>
              <p className="font-medium">本租户已关闭真人照片上传</p>
              <p className="text-muted-foreground">
                治理开关(dhAllowRealPhoto)由租户管理员在租户设置里控制,打开后才能走老板 IP 采集流。
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {step === 1 && (
        <Card>
          <CardContent className="grid gap-6 py-5 lg:grid-cols-[3fr_2fr]">
            <div className="flex items-start gap-3">
              <div className="rounded-lg bg-primary/10 p-2">
                <Camera className="h-4 w-4 text-primary" />
              </div>
              <div className="max-w-2xl space-y-1 text-sm">
                <p className="font-medium">拍摄指引 —— 照片质量决定后面每一条片子</p>
                <ul className="list-disc space-y-0.5 pl-4 text-muted-foreground">
                  <li>正脸面对镜头,不侧头不低头,发不遮脸</li>
                  <li>光线均匀(窗边自然光最好),避免顶光/逆光的重阴影</li>
                  <li>背景干净(纯色墙面最好),画面里只有本人一个人</li>
                  <li>
                    <b className="font-medium text-foreground">请传原图</b>
                    (≥640px):微信传图/截图会压缩,人脸链路会拒
                  </li>
                </ul>
              </div>
            </div>
            <div className="flex flex-col justify-between gap-5">
              <div className="space-y-1.5">
                <Label htmlFor="real-name">人物名字 *</Label>
                <Input
                  id="real-name"
                  value={name}
                  maxLength={64}
                  placeholder="如:王总 / 老板"
                  className="max-w-sm"
                  onChange={(e) => setName(e.target.value)}
                />
              </div>
              <div className="flex justify-end">
                <Button disabled={!name.trim() || !allowPhoto} onClick={() => setStep(2)}>
                  下一步:传照片
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {step === 2 && (
        <Card>
          <CardContent className="space-y-4 py-5">
            <div className="flex items-start gap-4">
              <button
                type="button"
                className={cn(
                  "flex aspect-[3/4] w-40 shrink-0 flex-col items-center justify-center gap-2 overflow-hidden rounded-[calc(var(--radius)*1.5)] border border-dashed text-xs text-muted-foreground",
                  previewUrl && "border-solid"
                )}
                onClick={() => fileInputRef.current?.click()}
              >
                {previewUrl ? (
                  <img
                    src={previewUrl}
                    alt="待体检照片预览"
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <>
                    <ImagePlus className="h-6 w-6 opacity-50" />
                    选择照片
                  </>
                )}
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                aria-label="选择真人照片"
                onChange={(e) => pickFile(e.target.files)}
              />

              <div className="min-w-0 max-w-2xl flex-1 space-y-2 text-sm">
                <p className="font-medium">上传体检(当场报,不等出片才失败)</p>
                {!file && <p className="text-muted-foreground">选一张照片,马上告诉你能不能用。</p>}
                {tooSmall && (
                  <PrecheckLine
                    ok={false}
                    text="图太小(短边 <640px)—— 请传原图,别用微信压缩图/截图"
                  />
                )}
                {precheckMutation.isPending && <p className="text-muted-foreground">体检中…</p>}
                {!tooSmall &&
                  precheck &&
                  (precheck.checked ? (
                    precheck.pass ? (
                      <PrecheckLine ok text="体检通过:人脸清晰可用" />
                    ) : (
                      <PrecheckLine
                        ok={false}
                        // B2 #22(0927 K2):reason 曾是接口原文(HTTP 400 + JSON),原样印给用户;只放人话,其余兜底句
                        text={`体检不过:${facePrecheckReasonText(precheck.reason)} —— 换一张再试`}
                      />
                    )
                  ) : (
                    <p className="text-muted-foreground">
                      预检开关未启用,跳过体检 —— 出片链路仍有校验,建议照指引拍。
                    </p>
                  ))}
                {file && (
                  <>
                    <p className="text-muted-foreground">
                      再传 {MAX_EXTRA_PHOTOS} 张不同角度照片,人脸更稳(可选)
                      {extraFiles.length > 0 && `—— 已多选 ${extraFiles.length} 张`}
                    </p>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      换一张/多选
                    </Button>
                  </>
                )}
              </div>
            </div>

            {/* 口播视频槽:可选附加 → 推荐(SEEDANCE 真人出镜 P0,0914 车 D5②)——
                ★不替代照片 —— 后端红线是定妆图必须照片(from-avatar-profile 拒 video kind),
                视频作为第二份形象资产入「我的形象」,出片走视频换口播。预检端点只吃图,视频体检
                走本地闸(checkVideoFile,D5③,§B4 同份约束)。
                ★排版(0915 老板走查「上面是图片,下面是口播视频,显示不协调」):与上方照片槽
                **同构** —— 左侧同尺寸预览框(选中后播视频预览),右侧说明文字;不再是一条
                「图标+文案+右侧小按钮」的横幅,两块上下对齐成同一套上传语言。 */}
            <div className="flex items-start gap-4">
              <button
                type="button"
                className={cn(
                  "flex aspect-[3/4] w-40 shrink-0 flex-col items-center justify-center gap-2 overflow-hidden rounded-[calc(var(--radius)*1.5)] border border-dashed text-xs text-muted-foreground",
                  videoPreviewUrl && "border-solid"
                )}
                onClick={() => videoInputRef.current?.click()}
              >
                {videoPreviewUrl ? (
                  <video
                    src={videoPreviewUrl}
                    muted
                    playsInline
                    loop
                    autoPlay
                    className="h-full w-full object-cover"
                    aria-label="口播视频预览"
                  />
                ) : (
                  <>
                    <Clapperboard className="h-6 w-6 opacity-50" />
                    选口播视频
                  </>
                )}
              </button>
              <input
                ref={videoInputRef}
                type="file"
                accept={VIDEO_ACCEPT}
                className="hidden"
                aria-label="选择口播视频(推荐)"
                onChange={(e) => void pickVideo(e.target.files?.[0])}
              />

              <div className="min-w-0 max-w-2xl flex-1 space-y-2 text-sm">
                <div className="flex items-center gap-1.5">
                  <p className="font-medium">口播视频(可选)</p>
                  <Badge variant="secondary" className="text-2xs">
                    推荐
                  </Badge>
                </div>
                <p className="text-foreground">上传一段 5~15 秒正脸清晰视频,效果比照片更稳。</p>
                <p className="text-muted-foreground">
                  mp4/mov、带脸带声,超过 15 秒会自动截前 15 秒:随同一份授权存进「我的素材」,
                  出片走视频换口播 —— 最像真人、能用原声。人物定妆图仍用上面的照片。
                </p>
                {videoChecking && <p className="text-muted-foreground">体检中…</p>}
                {videoFile && !videoChecking && !videoIssue && (
                  // ★措辞避开「体检通过」——那四个字是照片预检结果行的专属锚(测试也按它锁),
                  // 两行同字样会把"照片过没过"这个关键信号淹掉。
                  <PrecheckLine ok text={`已选「${videoFile.name}」,达标 · 入库时一并保存`} />
                )}
                {videoIssue && !videoChecking && (
                  <PrecheckLine
                    ok={false}
                    text={
                      videoIssue.blocksSubmit
                        ? `${videoIssue.text}(这个文件不会入库)`
                        : `${videoIssue.text}(仍会入库,仅提醒)`
                    }
                  />
                )}
                {videoFile && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => videoInputRef.current?.click()}
                  >
                    换一个
                  </Button>
                )}
              </div>
            </div>

            <div className="flex justify-between">
              <Button variant="outline" onClick={() => setStep(1)}>
                上一步
              </Button>
              <Button disabled={!canNext2} onClick={() => setStep(3)}>
                下一步:授权入库
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {step === 3 && (
        <Card>
          <CardContent className="space-y-4 py-5">
            <div className="flex items-start gap-4">
              {previewUrl && (
                <img
                  src={previewUrl}
                  alt="待入库照片"
                  className="aspect-[3/4] w-32 shrink-0 rounded-[calc(var(--radius)*1.5)] object-cover"
                />
              )}
              <div className="max-w-2xl space-y-3 text-sm">
                <p className="font-medium">授权存证(真人红线)</p>
                <p className="text-muted-foreground">
                  真人形象是本人生物特征资产:本人授权入库后全公司共用出片,跨公司不可用;产物全程带
                  AIGC 标识,授权记录随档案存证。
                </p>
                {/* D5①(SEEDANCE 真人出镜 P0,0914 车 D):逐字追加 —— P0 走 vapeur 免扫码通道,
                    这句话解释「现在为什么不用扫码」以及「以后切官方通道要补一次」这个真实约束。 */}
                <p className="text-muted-foreground">
                  当前通道无需本人扫码认证；如后续切换官方通道，需人物本人补扫一次。
                </p>
                {videoFile && !videoIssue?.blocksSubmit && (
                  <p className="text-muted-foreground">
                    将同时保存口播视频「{videoFile.name}」到「我的素材」(出片可直接选用)。
                  </p>
                )}
                <label htmlFor="real-consent" className="flex items-start gap-2">
                  <Checkbox
                    id="real-consent"
                    checked={consent}
                    onCheckedChange={(v) => setConsent(v === true)}
                    aria-label="肖像授权确认"
                    className="mt-0.5"
                  />
                  <span>
                    我已获得肖像本人授权,同意保存该照片
                    {videoFile && !videoIssue?.blocksSubmit ? "与口播视频" : ""}并用于生成内容。
                  </span>
                </label>
              </div>
            </div>
            <div className="flex justify-between">
              <Button variant="outline" onClick={() => setStep(2)} disabled={enroll.isPending}>
                上一步
              </Button>
              <Button disabled={!consent || enroll.isPending} onClick={() => enroll.mutate()}>
                {enroll.isPending ? "入库中…" : `建档「${name.trim()}」`}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* 第 4 步(可选):人物已建档,顺手把声纹也克隆了并挂上 —— 出片「按人物选」
          就能一键带出形象+声音。录音卡由 App 层注入(FSD),跳过随时可走,没有回头路
          (人物已成立,回上一步没有意义)。 */}
      {step === 4 && created && voiceEnrollSlot && (
        <Card>
          <CardContent className="space-y-4 py-5">
            <div className="max-w-2xl space-y-1 text-sm">
              <p className="font-medium">顺手把声纹也克隆了(可选)</p>
              <p className="text-muted-foreground">
                「{created.name}」已建档。现在朗读引导文本(10 秒以上,录到 19
                秒自动停),克隆的音色会自动挂到这个人物 ——
                出片「按人物选」即可同时带出形象与声音。也可以先跳过,之后在声音库或人物详情
                「真人原料」里随时补。
              </p>
            </div>
            {voiceEnrollSlot(linkVoice)}
            <div className="flex justify-end">
              <Button
                variant="outline"
                disabled={voiceSaving}
                onClick={() => navigate(exitTo(created.id))}
              >
                {returnTo ? "跳过,回去选人" : "跳过,去人物详情"}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function PrecheckLine({ ok, text }: { ok: boolean; text: string }) {
  const Icon = ok ? CheckCircle2 : XCircle;
  return (
    <p className={cn("flex items-start gap-1.5", ok ? "text-emerald-600" : "text-destructive")}>
      <Icon className="mt-0.5 h-4 w-4 shrink-0" />
      {text}
    </p>
  );
}
